import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ColorPicker from '@/components/basicComponents/colorPicker';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select from '@/components/basicComponents/select';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { SettingsContainer, SettingsSection } from '@/components/settings-page';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { useClickOutside } from '@/hooks/useClickOutside';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { fontSelect } from '../constants';
import { IFirmSettings, IFontStyle } from '../types';

interface EditFontStyleProps {
  currentRow: IFirmSettings | null;
  saveSettingValue: (key: string, value: string | IFontStyle) => Promise<void>;
  handleClose: () => void;
}

export const EditFontStyle = ({ currentRow, saveSettingValue, handleClose }: EditFontStyleProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const [searchParams] = useSearchParams();

  const edit = searchParams.get('edit') || '';

  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<{ title: string; description: string }>(null);
  const [loading, setLoading] = useState(false);
  const [chosenFont, setChosenFont] = useState<{ fontFamily: string; fontColor: string; fontWeight: string }>({
    fontFamily: '',
    fontColor: '',
    fontWeight: ''
  });

  const colorPickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (currentRow?.value) {
      setChosenFont({
        fontFamily: currentRow.value.fontFamily,
        fontColor: currentRow.value.fontColor,
        fontWeight: currentRow.value.fontWeight
      });
    }
    return () => {
      setChosenFont({
        fontFamily: '',
        fontColor: '',
        fontWeight: ''
      });
    };
  }, [currentRow]);

  useEffect(() => {
    if (!currentRow?.value) {
      setChosenFont({
        fontFamily: edit === 'headerFontStyle' ? 'Sanchez' : 'Open Sans',
        fontColor: edit === 'headerFontStyle' ? '35739C' : '757575',
        fontWeight: edit === 'headerFontStyle' ? '400' : '600'
      });
    }
  }, [edit]);

  const validateInput = (value: string) => {
    const regex = /^[0-9a-f]{0,6}$/i;

    if (!regex.test(value)) {
      setErrorMessage({
        title: 'Please enter a valid hexidecimal value for the Header Font Color',
        description: 'Provide six characters using only 0 - 9 and a - f'
      });
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const saveValue = async () => {
    if (!validateInput(chosenFont.fontColor)) return;

    try {
      setErrorMessage(null);
      setLoading(true);

      const defaultColor = currentRow?.key === 'headerFontStyle' ? '35739C' : '757575';
      const fontValues = {
        fontFamily: chosenFont.fontFamily,
        fontColor: chosenFont.fontColor.length ? chosenFont.fontColor : defaultColor,
        fontWeight: chosenFont.fontWeight
      };

      await saveSettingValue(currentRow?.key, fontValues);
      handleClose();
    } catch (error) {
      setErrorMessage({ title: 'Error', description: defaultErrorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleColorPicker = () => {
    setIsColorPickerOpen(!isColorPickerOpen);
  };

  useClickOutside<HTMLDivElement>(colorPickerRef, handleColorPicker);

  return (
    <>
      {loading && (
        <ModalWrappedContent
          isOpen={loading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="Templates" />} />
        <PageTitle title={currentRow?.fieldName ?? ''} />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title={errorMessage.title}
            description={errorMessage.description}
            bgColor={themeVar('context.error')}
          />
        )}
        <SettingsContainer>
          <SettingsSection isMobile={isMobile}>
            <Banner
              title=""
              description="Choose a darker color that works well on a white or light colored background. This will be used for titles and section headers."
              icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
              bgColor={themeVar('layer.base')}
              color={themeVar('font.base')}
              dividerColor={themeVar('border.base')}
            />
            <FontStyleWrapper isMobile={isMobile}>
              <Select
                options={Object.keys(fontSelect)?.map((item) => ({ label: item, value: item }))}
                value={chosenFont.fontFamily}
                setValue={(value) =>
                  setChosenFont({
                    ...chosenFont,
                    fontWeight: fontSelect[value as keyof typeof fontSelect][0],
                    fontFamily: value
                  })
                }
                label="Header Font Family"
              />
              <InputWrapper>
                <Input
                  value={chosenFont.fontColor || ''}
                  onChange={(event) =>
                    setChosenFont({
                      ...chosenFont,
                      fontColor: event.target.value
                    })
                  }
                  label="Header Font Color"
                />
                <ColorPreview bgColor={`#${chosenFont.fontColor}`} onClick={handleColorPicker} />
                {isColorPickerOpen && (
                  <Popover ref={colorPickerRef}>
                    <PopoverContent>
                      <ColorPicker
                        color={chosenFont.fontColor}
                        onChange={(color) =>
                          setChosenFont({
                            ...chosenFont,
                            fontColor: color
                          })
                        }
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </InputWrapper>
              <Select
                options={fontSelect[chosenFont.fontFamily as keyof typeof fontSelect]?.map((item) => ({ label: item, value: item }))}
                value={chosenFont.fontWeight}
                setValue={(value) => setChosenFont({ ...chosenFont, fontWeight: value })}
                label="Header Font Weight"
              />
            </FontStyleWrapper>
          </SettingsSection>
          <FormFooter onCancel={handleClose} onSave={saveValue} disableSaveButton={loading} showRequiredFieldsWarning={false} />
        </SettingsContainer>
      </MainWrap>
    </>
  );
};

const FontStyleWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : 'repeat(3, 1fr)')};
  gap: 10px;
  padding-top: 30px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const ColorPreview = styled.div<{ bgColor: string }>`
  position: absolute;
  top: 49px;
  right: 12px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  border-radius: 20px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${({ bgColor }) => bgColor};
`;

const Popover = styled.div`
  position: absolute;
  z-index: 2;
  top: 100px;
  left: 0;
`;

const PopoverContent = styled.div`
  position: relative;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
`;
